import { createRouter, createWebHistory } from 'vue-router'
import { getTokens, isSignedIn } from '@dacr/auth'
import { decodeJWT } from '@/shared/utils/jwt'
import { UserPermission, type User } from '@/shared/graphql/legacy/generated'
import { useWebviewStore } from '@/shared/stores/webview'
import { useBillingStore } from '@/features/billing/store'
import { pricingCheckout } from '@/features/onboarding/pricingCheckout'
import { FeatureFlag } from '@/shared/stores/featureFlags'
import { authGuard, featureFlagGuard } from './guards'
import { setupMiddleware } from './middleware'
import { folderTitle, fileSearchTitle, publicSearchTitle } from './titles'

// Auth
import LoginView from '@/features/auth/views/LoginView.vue'
import OAuthAuthorizeView from '@/features/auth/views/OAuthAuthorizeView.vue'
import RegisterView from '@/features/auth/views/RegisterView.vue'
import RegisterFormView from '@/features/auth/views/RegisterFormView.vue'
import RegisterOrganizationView from '@/features/auth/views/RegisterOrganizationView.vue'
import RegisterVerifyIdentityView from '@/features/auth/views/RegisterVerifyIdentityView.vue'
import AuthView from '@/features/auth/views/AuthView.vue'
import AuthSuccessView from '@/features/auth/views/AuthSuccessView.vue'
import ForgotPasswordView from '@/features/auth/views/ForgotPasswordView.vue'
import ResetPasswordView from '@/features/auth/views/ResetPasswordView.vue'
import SignoutView from '@/features/auth/views/SignoutView.vue'

// Onboarding
import OnboardingView from '@/features/onboarding/views/OnboardingView.vue'
import OnboardingStepsView from '@/features/onboarding/views/OnboardingStepsView.vue'
import OnboardingSelectPlanView from '@/features/onboarding/views/OnboardingSelectPlanView.vue'

// Billing
import CheckoutView from '@/features/billing/views/CheckoutView.vue'
import UpgradeView from '@/features/billing/views/UpgradeView.vue'
import UpgradeSelectPlanView from '@/features/billing/views/UpgradeSelectPlanView.vue'
import ChangePlansView from '@/features/billing/views/ChangePlansView.vue'
import ChangePlansSelectPlanView from '@/features/billing/views/ChangePlansSelectPlanView.vue'
import ChangePlansConfirmView from '@/features/billing/views/ChangePlansConfirmView.vue'

// Explorer
import FileExplorerView from '@/features/explorer/views/FileExplorerView.vue'
import FileExplorerFolderView from '@/features/explorer/views/FileExplorerFolderView.vue'
import ArchiveView from '@/features/explorer/views/ArchiveView.vue'

// Settings
import SettingsView from '@/features/settings/views/SettingsView.vue'
import SettingsAccountView from '@/features/settings/views/SettingsAccountView.vue'
import SettingsBillingView from '@/features/settings/views/SettingsBillingView.vue'
import SettingsSecurityView from '@/features/settings/views/SettingsSecurityView.vue'

// Copyright
import CopyrightView from '@/features/copyright/views/CopyrightView.vue'
import TranscriptView from '@/features/copyright/views/TranscriptView.vue'
import SheetMusicView from '@/features/copyright/views/SheetMusicView.vue'

// Infringement
import InfringementView from '@/features/infringement/views/InfringementView.vue'
import InfringementListView from '@/features/infringement/views/InfringementListView.vue'
import InfringementReportView from '@/features/infringement/views/InfringementReportView.vue'

// Search
import FileSearchResultsView from '@/features/search/views/FileSearchResultsView.vue'
import PublicSearchResultsView from '@/features/search/views/PublicSearchResultsView.vue'

// Misc
import NotFoundView from '@/shared/components/NotFoundView.vue'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior: (_, __, savedPosition) => savedPosition ?? { top: 0 },
  routes: [
    {
      path: '/',
      redirect: '/home',
    },
    {
      path: '/login',
      name: 'Login',
      component: LoginView,
      meta: { layout: 'basic', title: 'Sign in' },
      beforeEnter(_, __, next) {
        if (isSignedIn()) {
          return next('/home')
        }

        next()
      },
    },
    {
      path: '/oauth/authorize',
      name: 'OAuthAuthorize',
      component: OAuthAuthorizeView,
      meta: { layout: 'default', title: 'Authorize app' },
    },
    {
      path: '/register',
      component: RegisterView,
      meta: { title: 'Create account' },
      beforeEnter(to, __, next) {
        if (isSignedIn() && !to.query.ott) {
          next('/home')
        }

        if (isSignedIn() && to.query.ott) {
          next({
            path: '/auth',
            query: {
              ott: to.query.ott,
            },
          })
        }

        if (to.name === 'RegisterRootView') {
          next()
        } else {
          next('/register')
        }
      },
      children: [
        {
          path: '',
          name: 'RegisterRootView',
          component: RegisterFormView,
        },
        {
          path: 'organization',
          name: 'RegisterOrganizationView',
          component: RegisterOrganizationView,
          meta: { title: 'Register organization' },
        },
        {
          path: 'identity',
          name: 'RegisterVerifyIdentityView',
          component: RegisterVerifyIdentityView,
          meta: { title: 'Verify identity' },
          beforeEnter: [featureFlagGuard(FeatureFlag.Iden, '/onboarding')],
        },
      ],
    },
    {
      path: '/auth',
      meta: { layout: 'default' },
      beforeEnter: (to, _, next) => {
        if (!isSignedIn()) {
          return next({
            path: '/login',
            query: {
              ott: to.query.ott,
            },
          })
        }

        next()
      },
      children: [
        {
          path: '',
          name: 'AuthView',
          component: AuthView,
          meta: { title: 'Signing in' },
          beforeEnter: (to, _, next) => {
            if (!to.query.ott) {
              return next('/home')
            }

            next()
          },
        },
        {
          path: 'success',
          name: 'AuthSuccessView',
          component: AuthSuccessView,
          meta: { title: 'Sign-in successful' },
          beforeEnter: (_, from, next) => {
            if (
              !from.path.includes('login') &&
              !from.path.includes('register') &&
              !from.path.includes('auth')
            ) {
              return next('/home')
            }

            next()
          },
        },
      ],
    },
    {
      path: '/onboarding',
      name: 'Onboarding',
      component: OnboardingView,
      meta: { title: 'Welcome to Dacr' },
      children: [
        {
          path: '',
          name: 'OnboardingSteps',
          component: OnboardingStepsView,
        },
        {
          path: 'pro',
          name: 'OnboardingPro',
          component: OnboardingSelectPlanView,
          meta: { title: 'Choose your membership' },
        },
        {
          path: 'checkout',
          name: 'OnboardingCheckout',
          component: CheckoutView,
          meta: { title: 'Checkout' },
          beforeEnter: (_, __, next) => {
            const planStore = useBillingStore()

            if (
              planStore.selectedPlan ||
              pricingCheckout.value?.stage === 'checkout'
            ) {
              return next()
            }

            next('/')
          },
        },
      ],
    },
    {
      path: '/forgot-password',
      name: 'ForgotPassword',
      component: ForgotPasswordView,
      meta: { layout: 'basic', title: 'Forgot password' },
    },
    {
      path: '/reset-password',
      name: 'ResetPassword',
      component: ResetPasswordView,
      meta: { layout: 'basic', title: 'Reset password' },
    },
    {
      path: '/upgrade',
      beforeEnter: [
        (_, __, next) => {
          const webviewStore = useWebviewStore()

          if (webviewStore.isActive) {
            return next()
          }

          if (!isSignedIn()) {
            return next('/login')
          }

          const userData = localStorage.getItem('dacrUser')

          if (!userData) {
            return next('/home')
          }

          const user = JSON.parse(userData || '{}') as User
          const billingStore = useBillingStore()

          if (
            user.permissions.includes(UserPermission.Pro) &&
            billingStore.billing &&
            !billingStore.isBasicMembership
          ) {
            return next('/home')
          }

          // Basic Membership carries the legacy Pro permission. On a cold load
          // the billing store is not hydrated yet, so let the view wait for the
          // billing query before deciding whether this is already a Pro member.
          return next()
        },
        (to, _, next) => {
          if (to.name === 'UpgradeCheckoutView') {
            next('/upgrade')
          } else {
            next()
          }
        },
      ],
      component: UpgradeView,
      meta: { title: 'Upgrade membership' },
      children: [
        {
          path: '',
          name: 'UpgradeSelectPlanView',
          component: UpgradeSelectPlanView,
        },
        {
          path: 'checkout',
          name: 'UpgradeCheckoutView',
          component: CheckoutView,
          meta: { title: 'Checkout' },
          beforeEnter: (_, __, next) => {
            const planStore = useBillingStore()

            if (planStore.selectedPlan) {
              return next()
            }

            next('/')
          },
        },
      ],
    },
    {
      path: '/change-plans',
      name: 'ChangePlansView',
      component: ChangePlansView,
      meta: { title: 'Change plans' },
      beforeEnter: authGuard,
      children: [
        {
          path: '',
          name: 'ChangePlansSelectPlanView',
          component: ChangePlansSelectPlanView,
        },
        {
          path: 'confirm',
          name: 'ChangePlansConfirmView',
          component: ChangePlansConfirmView,
          meta: { title: 'Confirm plan change' },
        },
        {
          path: 'checkout',
          name: 'ChangePlansCheckoutView',
          component: CheckoutView,
          meta: { title: 'Checkout' },
          beforeEnter: (_, __, next) => {
            const planStore = useBillingStore()

            if (planStore.selectedPlan) {
              return next()
            }

            next('/')
          },
        },
      ],
    },
    {
      path: '/home',
      component: FileExplorerView,
      meta: { layout: 'shell', sidebar: true },
      beforeEnter: authGuard,
      children: [
        {
          path: '',
          name: 'FileExplorerRootFolderView',
          component: FileExplorerFolderView,
          meta: { title: folderTitle },
        },
        {
          path: ':path+',
          name: 'FileExplorerFolderView',
          component: FileExplorerFolderView,
          meta: { title: folderTitle },
        },
      ],
    },
    {
      path: '/archive',
      component: ArchiveView,
      meta: { layout: 'shell', title: 'Archived registrations' },
      beforeEnter: authGuard,
    },
    {
      path: '/settings',
      component: SettingsView,
      meta: { layout: 'default' },
      beforeEnter(_, __, next) {
        const webviewStore = useWebviewStore()

        if (webviewStore.isActive) {
          const { accessToken } = getTokens()

          if (!accessToken || !decodeJWT(accessToken)) {
            return next('/404')
          }

          return next()
        }

        if (!isSignedIn()) {
          return next('/login')
        }

        next()
      },
      children: [
        {
          path: '',
          name: 'SettingsView',
          redirect: '/settings/account',
        },
        {
          path: 'account',
          name: 'SettingsAccountView',
          component: SettingsAccountView,
          meta: { title: 'Account' },
        },
        {
          path: 'billing',
          name: 'SettingsBillingView',
          component: SettingsBillingView,
          meta: { title: 'Plan & Billing' },
        },
        {
          path: 'security',
          name: 'SettingsSecurityView',
          component: SettingsSecurityView,
          meta: { title: 'Security' },
        },
      ],
    },
    {
      path: '/file/:id',
      meta: { layout: 'default' },
      children: [
        {
          path: '',
          component: CopyrightView,
          meta: { title: 'Copyright' },
          name: 'CopyrightView',
        },
        {
          path: 'transcript',
          component: TranscriptView,
          meta: { title: 'Transcript' },
          name: 'TranscriptView',
        },
        {
          path: 'tracks/:trackIndex/transcript',
          component: TranscriptView,
          meta: { title: 'Transcript' },
          name: 'TrackTranscriptView',
        },
        {
          path: 'tracks/:trackIndex/:stem/sheet-music',
          component: SheetMusicView,
          meta: { title: 'Sheet music' },
          name: 'TrackSheetMusicView',
        },
      ],
    },
    {
      path: '/infringement',
      name: 'InfringementView',
      component: InfringementView,
      meta: { layout: 'shell', title: 'Infringement Docket' },
      beforeEnter: [authGuard, featureFlagGuard(FeatureFlag.AI)],
      children: [
        {
          path: '',
          component: InfringementListView,
          name: 'InfringementListView',
        },
        {
          path: 'group/:groupId/:timestamp',
          component: InfringementListView,
          name: 'InfringementGroupView',
        },
      ],
    },
    {
      path: '/infringement/report/:id',
      name: 'InfringementReportView',
      component: InfringementReportView,
      meta: { layout: 'default', title: 'Infringement report' },
      beforeEnter: [featureFlagGuard(FeatureFlag.AI)],
    },
    {
      path: '/search',
      beforeEnter: authGuard,
      children: [
        {
          path: 'files',
          component: FileSearchResultsView,
          meta: { layout: 'shell', title: fileSearchTitle },
        },
        {
          path: 'public',
          component: PublicSearchResultsView,
          meta: { layout: 'default', title: publicSearchTitle },
        },
      ],
    },
    {
      path: '/checkout',
      component: CheckoutView,
      meta: { layout: 'default', title: 'Checkout' },
      beforeEnter: authGuard,
    },
    {
      path: '/signout',
      component: SignoutView,
      meta: { title: 'Signing out' },
      beforeEnter: authGuard,
    },
    {
      path: '/:notFound(.*)*',
      component: NotFoundView,
      meta: { layout: 'default', title: 'Page not found' },
    },
  ],
})

setupMiddleware(router)

export default router
